import React, { useEffect, useRef, useState, useCallback } from "react";
import { adminService } from "./adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import {
	Trash2,
	Plus,
	MapPin,
	RefreshCw,
	X,
	Upload,
	ChevronLeft,
	ChevronRight,
	ChevronsLeft,
	ChevronsRight,
	Search,
	AlertCircle,
} from "lucide-react";

/**
 * Pull a human-readable message out of whatever the failure was.
 * The backend reports errors as `{"error": "..."}`; a network/CORS failure has
 * no response at all, which must not be reported as a generic "failed" or the
 * operator has no way to tell a bad file from a dead session.
 */
const describeError = (err: any, fallback: string): string => {
	const data = err?.response?.data;
	if (data?.error) return data.error;
	if (data?.detail) return data.detail;
	if (err?.response?.status) return `${fallback} (HTTP ${err.response.status})`;
	if (err?.message) return `${fallback}: ${err.message}`;
	return fallback;
};

interface WhitelistPincodeEntry {
	id: number;
	pincode: string;
	added_on: string;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

// ManageWhitelistPincode — Caprion admin panel for the WhitelistPincode table.
// The table is read by the WHITELIST_PINCODE_LOCATION verification on the
// Initial Verification page: a lead passes only if at least one of their
// personal/business address pincodes is present here.
//
// Deliberately simpler than ManagePreApproved: only "create" (manual rows) and
// "Upload CSV" — no inline editing (a pincode is either whitelisted or not).
const ManageWhitelistPincode: React.FC = () => {
	const { toast } = useToast();
	const fileInputRef = useRef<HTMLInputElement>(null);

	// ── List + pagination state ───────────────────────────────────────────────
	const [entries, setEntries] = useState<WhitelistPincodeEntry[]>([]);
	const [total, setTotal] = useState(0);
	const [totalPages, setTotalPages] = useState(1);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [search, setSearch] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [loading, setLoading] = useState(false);

	// ── Add-form state ────────────────────────────────────────────────────────
	const [addRows, setAddRows] = useState<string[]>([""]);
	const [adding, setAdding] = useState(false);
	const [csvUploading, setCsvUploading] = useState(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	// Inline banner. A toast alone is too easy to miss (and auto-dismisses), so
	// upload/save failures are ALSO rendered in the card until the next attempt.
	const [errorMsg, setErrorMsg] = useState<string | null>(null);
	const [okMsg, setOkMsg] = useState<string | null>(null);

	// ── Delete state ──────────────────────────────────────────────────────────
	const [deletingId, setDeletingId] = useState<number | null>(null);

	// ─────────────────────────────────────────────────────────────────────────
	// Fetch
	// ─────────────────────────────────────────────────────────────────────────
	const fetchEntries = useCallback(async (p = page, ps = pageSize, s = search) => {
		setLoading(true);
		try {
			const res = await adminService.listWhitelistPincode({ page: p, page_size: ps, search: s || undefined });
			const data = res.data;

			// NEVER trust the response shape here. When the backend build lacks this
			// endpoint, Django returns 404 and nginx answers with the SPA's index.html
			// at HTTP 200 (and 304 on subsequent cached requests) — so axios does NOT
			// throw and `data` is an HTML STRING, not our JSON. Assigning
			// `data.results` (undefined) into `entries` then crashed `entries.length`
			// during render, which blanked the ENTIRE admin dashboard rather than just
			// this panel. Fail loudly in this panel instead.
			if (!data || typeof data !== "object" || !Array.isArray(data.results)) {
				console.error(
					"[WhitelistPincode] Unexpected response for list endpoint — expected JSON " +
					"with a `results` array. This usually means /api/admin/whitelist-pincode/ " +
					"is not deployed on this environment (Django 404 → nginx SPA fallback).",
					{ status: res.status, dataType: typeof data, data }
				);
				setEntries([]);
				setTotal(0);
				setTotalPages(1);
				setErrorMsg(
					"Could not load whitelisted pincodes: the API returned an unexpected " +
					"response. The whitelist-pincode endpoint may not be deployed on this environment."
				);
				return;
			}

			setEntries(data.results);
			setTotal(typeof data.total === "number" ? data.total : data.results.length);
			setTotalPages(typeof data.total_pages === "number" && data.total_pages > 0 ? data.total_pages : 1);
			setPage(typeof data.page === "number" && data.page > 0 ? data.page : p);
		} catch (err: any) {
			console.error("[WhitelistPincode] list request failed:", err);
			setEntries([]);
			setTotal(0);
			setTotalPages(1);
			const msg = describeError(err, "Failed to load whitelisted pincodes");
			setErrorMsg(msg);
			toast({ title: msg, variant: "destructive" });
		} finally {
			setLoading(false);
		}
	}, [page, pageSize, search]);

	useEffect(() => {
		fetchEntries(page, pageSize, search);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, pageSize, search]);

	// ─────────────────────────────────────────────────────────────────────────
	// Search
	// ─────────────────────────────────────────────────────────────────────────
	const handleSearchSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		setSearch(searchInput.trim());
		setPage(1);
	};

	const handleSearchClear = () => {
		setSearchInput("");
		setSearch("");
		setPage(1);
	};

	// ─────────────────────────────────────────────────────────────────────────
	// Pagination helpers
	// ─────────────────────────────────────────────────────────────────────────
	const goToPage = (p: number) => {
		setPage(Math.max(1, Math.min(p, totalPages)));
	};

	const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setPageSize(Number(e.target.value));
		setPage(1);
	};

	// ─────────────────────────────────────────────────────────────────────────
	// Add — manual rows
	// ─────────────────────────────────────────────────────────────────────────
	const handleAddRow = () => setAddRows((prev) => [...prev, ""]);

	const handleRemoveAddRow = (idx: number) =>
		setAddRows((prev) => prev.filter((_, i) => i !== idx));

	const handleAddRowChange = (idx: number, value: string) =>
		setAddRows((prev) => prev.map((r, i) => (i === idx ? value : r)));

	const handleAdd = async () => {
		const payload = addRows.map((p) => p.trim()).filter(Boolean);
		setErrorMsg(null);
		setOkMsg(null);
		if (payload.length === 0) {
			setErrorMsg("Enter at least one pincode first.");
			return;
		}
		setAdding(true);
		try {
			const res = await adminService.addWhitelistPincode(payload);
			const { added = [], skipped = [], added_count, skipped_count } = res.data;
			const addedTotal = added_count ?? added.length;
			const skippedTotal = skipped_count ?? skipped.length;
			if (addedTotal > 0) {
				const msg = `Added ${addedTotal} pincode${addedTotal === 1 ? "" : "s"}: ${added.map((a: any) => a.pincode).join(", ")}`;
				setOkMsg(msg);
				toast({ title: msg });
			}
			if (skippedTotal > 0) {
				const msg = `Skipped ${skippedTotal}: ` +
					skipped.map((s: any) => `${s.pincode} (${s.reason})`).join(", ");
				setErrorMsg(msg);
				toast({ title: msg, variant: "destructive" });
			}
			setAddRows([""]);
			setPage(1);
			fetchEntries(1, pageSize, search);
		} catch (err: any) {
			const msg = describeError(err, "Failed to add pincodes");
			setErrorMsg(msg);
			toast({ title: msg, variant: "destructive" });
		} finally {
			setAdding(false);
		}
	};

	// ─────────────────────────────────────────────────────────────────────────
	// Add — CSV upload
	// ─────────────────────────────────────────────────────────────────────────
	// Upload the given file. Kept separate from the <input> change handler so the
	// same code path serves both "auto-upload on pick" and the explicit Upload
	// button (a retry after a failure must not require re-picking the file).
	const uploadFile = async (file: File) => {
		console.log("[WhitelistPincode] uploadFile()", {
			name: file.name,
			size: file.size,
			type: file.type,
		});
		setErrorMsg(null);
		setOkMsg(null);
		setCsvUploading(true);
		try {
			console.log("[WhitelistPincode] POST /api/admin/whitelist-pincode/add/ …");
			const res = await adminService.addWhitelistPincodeCsv(file);
			console.log("[WhitelistPincode] response", res.status, res.data);
			const { added = [], skipped = [], added_count, skipped_count } = res.data;
			// The backend returns COUNTS plus a capped sample (a full 9k-row echo
			// was a ~380 KB payload), so never derive totals from the arrays.
			const addedTotal = added_count ?? added.length;
			const skippedTotal = skipped_count ?? skipped.length;

			const parts: string[] = [];
			if (addedTotal > 0) parts.push(`added ${addedTotal}`);
			if (skippedTotal > 0) parts.push(`skipped ${skippedTotal}`);

			if (addedTotal > 0) {
				setOkMsg(`${file.name}: ${parts.join(", ")}.`);
				toast({ title: `Uploaded ${file.name}`, description: parts.join(", ") });
			}
			if (skippedTotal > 0) {
				const detail = skipped
					.slice(0, 15)
					.map((s: any) => `${s.pincode} (${s.reason})`)
					.join(", ");
				const msg = `Skipped ${skippedTotal}${detail ? `: ${detail}` : ""}` +
					(skippedTotal > skipped.length ? ` … +${skippedTotal - skipped.length} more` : "");
				setErrorMsg(msg);
				toast({ title: msg, variant: "destructive" });
			}
			if (addedTotal === 0 && skippedTotal === 0) {
				const msg = `${file.name}: no pincodes found in the file.`;
				setErrorMsg(msg);
				toast({ title: msg, variant: "destructive" });
			}
			setSelectedFile(null);
			setPage(1);
			fetchEntries(1, pageSize, search);
		} catch (err: any) {
			console.error("[WhitelistPincode] upload failed", {
				message: err?.message,
				status: err?.response?.status,
				data: err?.response?.data,
				err,
			});
			const msg = describeError(err, "Upload failed");
			setErrorMsg(msg);
			toast({ title: msg, variant: "destructive" });
		} finally {
			setCsvUploading(false);
		}
	};

	// Fires when the user picks a file in the native picker.
	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0] ?? null;
		console.log("[WhitelistPincode] file input change — file:", file?.name ?? "(none)");
		setSelectedFile(file);
		setErrorMsg(null);
		setOkMsg(null);
		if (file) {
			// Upload straight away; the Upload button below is only needed for a retry.
			void uploadFile(file);
		}
	};

	// ─────────────────────────────────────────────────────────────────────────
	// Delete
	// ─────────────────────────────────────────────────────────────────────────
	const handleDelete = async (entry: WhitelistPincodeEntry) => {
		if (!window.confirm(`Remove pincode ${entry.pincode} from the whitelist?`)) return;
		setDeletingId(entry.id);
		try {
			const res = await adminService.deleteWhitelistPincode(entry.id);
			toast({ title: `Removed ${res.data.pincode}` });
			const newTotalPages = Math.max(1, Math.ceil((total - 1) / pageSize));
			const newPage = Math.min(page, newTotalPages);
			if (newPage !== page) {
				setPage(newPage);
				fetchEntries(newPage, pageSize, search);
			} else {
				fetchEntries(page, pageSize, search);
			}
		} catch {
			toast({ title: "Failed to delete", variant: "destructive" });
		} finally {
			setDeletingId(null);
		}
	};

	// ─────────────────────────────────────────────────────────────────────────
	// Render
	// ─────────────────────────────────────────────────────────────────────────
	// Hard guarantee that the render path only ever sees an array, whatever the
	// API returned (see the shape check in fetchEntries for why this matters).
	const safeEntries = Array.isArray(entries) ? entries : [];
	const startRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
	const endRow = Math.min(page * pageSize, total);

	return (
		<div className="max-w-5xl w-full space-y-6">

			{/* ── Add Pincodes ────────────────────────────────────────────────── */}
			<div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-4">
				<div>
					<h3 className="text-lg font-bold text-gray-900 mb-1">Add Whitelist Pincodes</h3>
					<p className="text-sm text-gray-500">
						Enter a 6-digit pincode, or upload a CSV. A lead passes the location check if any
						of their personal or business address pincodes is whitelisted here.
					</p>
				</div>

				{/* Column header for add rows */}
				<div className="grid grid-cols-[1fr_auto] gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">
					<span>Pincode</span>
					<span />
				</div>

				{/* Manual rows */}
				<div className="space-y-2">
					{addRows.map((row, idx) => (
						<div key={idx} className="grid grid-cols-[1fr_auto] gap-2 items-center">
							<div className="flex items-center gap-1.5">
								<MapPin className="h-4 w-4 text-gray-400 shrink-0" />
								<Input
									placeholder="Pincode (6 digits)"
									value={row}
									onChange={(e) => handleAddRowChange(idx, e.target.value)}
									className="font-mono text-sm"
									maxLength={6}
								/>
							</div>
							{addRows.length > 1 ? (
								<Button
									variant="ghost"
									size="icon"
									onClick={() => handleRemoveAddRow(idx)}
									className="text-gray-400 hover:text-red-500 shrink-0"
								>
									<X className="h-4 w-4" />
								</Button>
							) : (
								<span className="w-8" />
							)}
						</div>
					))}
				</div>

				<div className="flex flex-wrap gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={handleAddRow}
						className="gap-1.5 text-gray-600"
					>
						<Plus className="h-3.5 w-3.5" />
						Add row
					</Button>
					{/* Plain <button> for the same reason as the upload label above: the
					    shared Button wrapper intercepts onClick for its loading state. */}
					<button
						type="button"
						onClick={handleAdd}
						disabled={adding}
						className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-intellend-green hover:bg-intellend-green/90 text-white text-sm font-medium disabled:opacity-50"
					>
						<Plus className="h-4 w-4" />
						{adding ? "Adding…" : "Save Pincodes"}
					</button>

				</div>

				{/* ── File upload ───────────────────────────────────────────────────
					   A VISIBLE native <input type="file"> on purpose. Earlier revisions
					   used a hidden input driven by `fileInputRef.current.click()` behind
					   the shared <Button>, which intercepts onClick for its own loading
					   state — the programmatic click got swallowed, the picker never
					   opened, and no request was ever sent. A plain visible input removes
					   every layer of indirection: the browser opens the picker itself.
					   Picking a file uploads immediately; the button is only for retries. */}
				<div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
					<input
						ref={fileInputRef}
						id="whitelist-pincode-file"
						type="file"
						accept=".csv,.xlsx"
						onChange={handleFileChange}
						disabled={csvUploading}
						className="text-sm text-gray-600 file:mr-3 file:h-9 file:px-4 file:rounded-md file:border file:border-input file:bg-background file:text-sm file:font-medium file:text-gray-700 hover:file:bg-accent file:cursor-pointer disabled:opacity-50"
					/>
					<button
						type="button"
						onClick={() => {
							const file = selectedFile ?? fileInputRef.current?.files?.[0] ?? null;
							console.log("[WhitelistPincode] Upload button clicked — file:", file?.name ?? "(none)");
							if (!file) {
								setErrorMsg("Choose a .csv or .xlsx file first.");
								return;
							}
							void uploadFile(file);
						}}
						disabled={csvUploading}
						className="inline-flex items-center gap-2 h-9 px-4 rounded-md border border-input bg-background text-sm font-medium text-gray-600 hover:bg-accent disabled:opacity-50"
					>
						<Upload className="h-4 w-4" />
						{csvUploading ? "Uploading…" : "Upload"}
					</button>
				</div>

				{/* Inline result banners — a toast auto-dismisses and is easy to miss, so
					   the outcome of the last save/upload stays visible here. */}
				{errorMsg && (
					<div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
						<AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
						<span className="flex-1 break-words">{errorMsg}</span>
						<button
							type="button"
							onClick={() => setErrorMsg(null)}
							className="text-red-400 hover:text-red-600 shrink-0"
							aria-label="Dismiss error"
						>
							<X className="h-3.5 w-3.5" />
						</button>
					</div>
				)}
				{okMsg && (
					<div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 break-words">
						{okMsg}
					</div>
				)}

				<p className="text-xs text-gray-400">
					Accepts <code className="bg-gray-100 px-1 rounded">.csv</code> and{" "}
					<code className="bg-gray-100 px-1 rounded">.xlsx</code>: a single column with header{" "}
					<code className="bg-gray-100 px-1 rounded">pincode</code> or{" "}
					<code className="bg-gray-100 px-1 rounded">input.pincode</code>, one 6-digit
					pincode per row.
				</p>
			</div>

			{/* ── Current List ─────────────────────────────────────────────────── */}
			<div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
				{/* Header row */}
				<div className="flex flex-wrap items-center justify-between gap-3 mb-4">
					<div>
						<h3 className="text-lg font-bold text-gray-900">Current Whitelisted Pincodes</h3>
						<p className="text-sm text-gray-500">
							{total} pincode{total !== 1 ? "s" : ""} on file
							{search && (
								<span className="ml-1 text-intellend-green font-medium">(filtered)</span>
							)}
						</p>
					</div>
					<div className="flex items-center gap-2">
						{/* Search */}
						<form onSubmit={handleSearchSubmit} className="flex items-center gap-1">
							<div className="relative">
								<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
								<Input
									value={searchInput}
									onChange={(e) => setSearchInput(e.target.value)}
									placeholder="Search pincode…"
									className="pl-8 h-8 text-sm w-40"
								/>
							</div>
							{searchInput && (
								<Button
									type="button"
									variant="ghost"
									size="icon"
									onClick={handleSearchClear}
									className="h-8 w-8 text-gray-400 hover:text-gray-600"
								>
									<X className="h-3.5 w-3.5" />
								</Button>
							)}
							<Button type="submit" size="sm" variant="outline" className="h-8 text-gray-600">
								Search
							</Button>
						</form>

						{/* Rows per page */}
						<select
							value={pageSize}
							onChange={handlePageSizeChange}
							className="h-8 rounded-md border border-gray-200 text-sm px-2 text-gray-600 focus:outline-none focus:ring-1 focus:ring-intellend-green"
						>
							{PAGE_SIZE_OPTIONS.map((n) => (
								<option key={n} value={n}>{n} / page</option>
							))}
						</select>

						<Button
							variant="outline"
							size="sm"
							onClick={() => fetchEntries(page, pageSize, search)}
							disabled={loading}
							className="gap-2 text-gray-600 h-8"
						>
							<RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
							Refresh
						</Button>
					</div>
				</div>

				{/* `safeEntries` (not `entries`) is used from here on: reading `.length`
				    or calling `.map()` on a non-array would throw during render, and an
				    exception in this panel unmounts the WHOLE admin dashboard. */}
				{loading ? (
					<div className="text-sm text-gray-400 py-6 text-center">Loading…</div>
				) : safeEntries.length === 0 ? (
					<div className="text-sm text-gray-400 py-6 text-center">
						{search ? "No pincodes match your search." : "No whitelisted pincodes yet."}
					</div>
				) : (
					<>
						<div className="divide-y divide-gray-100">
							{/* Header */}
							<div className="grid grid-cols-[1fr_auto_auto] gap-2 pb-2 text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">
								<span>Pincode</span>
								<span>Added</span>
								<span />
							</div>

							{safeEntries.map((entry) => (
								<div
									key={entry.id}
									className="grid grid-cols-[1fr_auto_auto] gap-2 items-center py-3 px-1"
								>
									<p className="font-mono font-semibold text-gray-800">{entry.pincode}</p>
									<p className="text-xs text-gray-400 whitespace-nowrap">
										{new Date(entry.added_on).toLocaleDateString("en-IN", {
											day: "2-digit",
											month: "short",
											year: "numeric",
										})}
									</p>
									<Button
										variant="ghost"
										size="icon"
										onClick={() => handleDelete(entry)}
										disabled={deletingId === entry.id}
										className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8"
									>
										<Trash2 className="h-4 w-4" />
									</Button>
								</div>
							))}
						</div>

						{/* ── Pagination controls ─────────────────────────────────────── */}
						{totalPages > 1 && (
							<div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
								<p className="text-xs text-gray-400">
									Showing {startRow}–{endRow} of {total}
								</p>

								<div className="flex items-center gap-1">
									<Button
										variant="ghost"
										size="icon"
										onClick={() => goToPage(1)}
										disabled={page === 1 || loading}
										className="h-7 w-7 text-gray-500 disabled:opacity-30"
										title="First page"
									>
										<ChevronsLeft className="h-4 w-4" />
									</Button>
									<Button
										variant="ghost"
										size="icon"
										onClick={() => goToPage(page - 1)}
										disabled={page === 1 || loading}
										className="h-7 w-7 text-gray-500 disabled:opacity-30"
										title="Previous page"
									>
										<ChevronLeft className="h-4 w-4" />
									</Button>

									<span className="px-2 text-xs text-gray-500 font-medium">
										Page {page} / {totalPages}
									</span>

									<Button
										variant="ghost"
										size="icon"
										onClick={() => goToPage(page + 1)}
										disabled={page === totalPages || loading}
										className="h-7 w-7 text-gray-500 disabled:opacity-30"
										title="Next page"
									>
										<ChevronRight className="h-4 w-4" />
									</Button>
									<Button
										variant="ghost"
										size="icon"
										onClick={() => goToPage(totalPages)}
										disabled={page === totalPages || loading}
										className="h-7 w-7 text-gray-500 disabled:opacity-30"
										title="Last page"
									>
										<ChevronsRight className="h-4 w-4" />
									</Button>
								</div>

								{/* Jump to page */}
								<form
									onSubmit={(e) => {
										e.preventDefault();
										const val = parseInt(
											(e.currentTarget.elements.namedItem("jumpPage") as HTMLInputElement).value,
											10,
										);
										if (!isNaN(val)) goToPage(val);
									}}
									className="flex items-center gap-1.5"
								>
									<span className="text-xs text-gray-400">Go to</span>
									<Input
										name="jumpPage"
										type="number"
										min={1}
										max={totalPages}
										defaultValue=""
										className="h-7 w-14 text-xs text-center font-mono"
										placeholder={String(page)}
									/>
									<Button type="submit" size="sm" variant="outline" className="h-7 text-xs px-2 text-gray-600">
										Go
									</Button>
								</form>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default ManageWhitelistPincode;
